import { useEffect } from "react";

import { useEditor } from "@tiptap/react";
import { ySyncPluginKey } from "@tiptap/y-tiptap";
import { runInAction } from "mobx";
import { useTranslations } from "next-intl";

import { SUGGESTIONS_MENU_ATTR } from "@/components/suggestions/constants";
import { corcaKitExtensions } from "@/components/tiptap/extensions";
import { findAndMergeExpressionNodes } from "@/components/tiptap/tiptap-expression/mergeExpressionNodes";
import {
  findDeletedCodeCells,
  findDeletedExpressionNodes,
  nodeIsExpression,
} from "@/components/tiptap/tiptap-expression/utils";
import { focusFirstNodeAfterTitle } from "@/components/tiptap/utils/focusFirstNodeAfterTitle";
import { installTiptapCommandLogger } from "@/components/tiptap/utils/tiptapCommandLogger";
import { useStore } from "@/store/RootStoreProvider";
import { teamPageUrl } from "@/urls";
import { getIsAndroid } from "@/utils/getIsAndroid";
import { getIsMobile } from "@/utils/getIsMobile";
import { getIsTouchDevice } from "@/utils/getIsTouchDevice";
import { createLogger } from "@/utils/logger";

import type { UploadScope } from "@/components/tiptap/utils/uploadScope";
import type { ChatMessageModel } from "@/models/chat/ChatMessageModel";
import type { ChatModel } from "@/models/chat/ChatModel";
import type { DocumentModel } from "@/models/DocumentModel";
import type { EditorProps } from "@tiptap/pm/view";
import type { Extendable } from "@tiptap/react";

const DEFAULT_CONTENT = "<h1></h1><p></p>";

const logger = createLogger("Tiptap");

export function useEditorCommon({
  doc,
  chat,
  message,
  uploadScope,
  editable,
  editorProps,
  extensions = [],
  autoFocus,
  onFocus,
  onBlur,
  immediatelyRender = typeof window !== "undefined",
  alwaysEager,
  mathIndentation,
}: {
  doc: DocumentModel;
  chat?: ChatModel;
  message?: ChatMessageModel;
  uploadScope?: UploadScope;
  editable: boolean;
  editorProps?: EditorProps;
  extensions?: Extendable[];
  autoFocus?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  immediatelyRender?: boolean;
  /** Disable lazy expression rendering — every expression renders eagerly. */
  alwaysEager?: boolean;
  mathIndentation?: boolean;
}) {
  const isMobile = getIsMobile();
  // Read documentTree lazily inside the callback: accessing it during an observer render
  // would mark the lazy DocumentTreeStore as observed and force-enable it for every editor.
  const rootStore = useStore();

  // TODO
  // https://github.com/ueberdosis/tiptap/issues/6725
  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const selector = "style[data-tiptap-style]";
    const tiptapStyle = document.querySelector(selector)?.innerHTML;
    if (!tiptapStyle) {
      return;
    }

    const ensureStylePresent = (): HTMLStyleElement | null => {
      let styleEl = document.querySelector<HTMLStyleElement>(selector);
      if (!styleEl) {
        styleEl = document.createElement("style");
        styleEl.setAttribute("data-tiptap-style", "");
        styleEl.innerHTML = tiptapStyle;
        document.head.appendChild(styleEl);
      }
      return styleEl;
    };

    ensureStylePresent();

    const observer = new MutationObserver(() => {
      if (!document.querySelector(selector)) {
        ensureStylePresent();
      }
    });

    observer.observe(document.head, {
      childList: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const t = useTranslations("tiptap");
  const editor = useEditor({
    immediatelyRender,
    editable,
    shouldRerenderOnTransaction: false,
    extensions: [
      ...extensions,
      ...corcaKitExtensions({
        doc,
        chat,
        message,
        uploadScope,
        alwaysEager,
        mathIndentation,
        onOpenCorcaDocumentLink: (docId) => rootStore.documentTree.openDocumentLink(docId),
        onOpenMention: (username) =>
          rootStore.router.push(teamPageUrl({ teamPermalink: username })),
        getAuthorId: () => rootStore.auth.currentUser?.uid,
      }),
    ],
    onBeforeCreate: ({ editor }) => {
      installTiptapCommandLogger(editor);
    },
    onCreate: ({ editor }) => {
      if (doc.yDoc?.getXmlFragment(doc.textNode.id).length === 0) {
        editor.commands.setContent(doc.textNode.value || DEFAULT_CONTENT);
      }
      const wantsAutoFocus = autoFocus ?? doc.autoFocus;
      if (
        wantsAutoFocus &&
        ((editable && !getIsTouchDevice()) || process.env.NODE_ENV === "test")
      ) {
        focusFirstNodeAfterTitle(editor, doc);
      }

      editor.view.dom.ariaBusy = "false";
    },
    onUpdate: ({ transaction }) => {
      const { isChangeOrigin, isUndoRedoOperation } =
        (transaction.getMeta(ySyncPluginKey) as Record<string, boolean>) || {};

      if (isUndoRedoOperation || isChangeOrigin) {
        logger.debug("Skip undo and remote operation");
        return;
      }

      findAndMergeExpressionNodes(transaction, doc);

      const deletedNodes = findDeletedExpressionNodes(transaction, doc);

      if (deletedNodes.length > 0) {
        doc.transact(() => {
          for (const node of deletedNodes) {
            doc.removeNode(node);
          }
        }, "Tiptap:cleanExpressionNodes");
      }

      const deletedCodeCells = findDeletedCodeCells(transaction);

      if (deletedCodeCells.length > 0) {
        doc.transact(() => {
          for (const cellId of deletedCodeCells) {
            doc.marimo.deleteResults(cellId);
          }
        }, "Tiptap:cleanDeletedCodeCells");
      }
    },
    onSelectionUpdate: ({ editor, transaction }) => {
      const isPasteTransaction = !!transaction.getMeta("paste");

      if ((!editor.isActive("inlineExpression") && !doc.cursor.isActive) || isPasteTransaction) {
        doc.cursor.reset();
      }

      if (!transaction.docChanged) {
        const { from: prevFrom, to: prevTo } = doc.editor.textSelection;

        if (prevFrom === prevTo && prevFrom >= 0 && prevFrom <= transaction.doc.content.size) {
          const $prevPos = transaction.doc.resolve(prevFrom);

          if (nodeIsExpression($prevPos.nodeBefore) && nodeIsExpression($prevPos.nodeAfter)) {
            findAndMergeExpressionNodes(transaction, doc);
          }
        }
      }

      const { from, to } = editor.state.selection;
      const { textSelection } = doc.editor;

      if (textSelection.from !== from || textSelection.to !== to) {
        runInAction(() => {
          doc.editor.textSelection = { from, to };
        });
      }
    },
    onFocus: () => {
      // Resetting cursor here leads to de-sync with selection, as onfocus fires before onSelectionUpdate
      onFocus?.();
    },
    onBlur: ({ event }) => {
      // Focus moving into the suggestions box (its search input) is not leaving the editor —
      // resetting here would tear down the menu the user is about to type into.
      if (
        event.relatedTarget instanceof Element &&
        event.relatedTarget.closest(`[${SUGGESTIONS_MENU_ATTR}]`)
      ) {
        return;
      }

      if (isMobile) {
        // On mobile, only reset suggestions when the keyboard is dismissed (e.g. "Done" button).
        // If the user is touching the suggestion box, keep them visible so the tap can complete.
        if (!doc.editor.suggestions.pointerTouch) {
          doc.editor.suggestions.mode = "idle";
        }
        // Text typed into math conversion ("x+") queues its trigger char for the math input.
        if (doc.isFocused) {
          doc.input.applyQueuedValueOnMathFocus();
        }
        return;
      }
      doc.editor.suggestions.reset(false);
      if (doc.isFocused) {
        doc.input.updateValueAfterFocus();
      } else {
        doc.editor.resetInput();
      }
      onBlur?.();
    },
    editorProps: {
      ...editorProps,
      attributes: (state) => ({
        enterKeyHint: "enter",
        // ProseMirror stringifies every value, so a read-only editor omits these instead.
        ...(editable
          ? { role: "textbox", "aria-multiline": "true", "aria-label": t("editorName") }
          : {}),
        "aria-busy": "true",
        // Android auto-capitalizes the first letter after an inline math node (it
        // reads the caret as the start of a new sentence). Disable it for the whole
        // editable on Android — in a math editor letter case is significant (x ≠ X),
        // so auto-capitalization is unwanted regardless. Scoped to Android to leave
        // iOS sentence-casing untouched; it's a no-op on physical keyboards.
        ...(getIsAndroid() ? { autocapitalize: "none" } : {}),
        ...(typeof editorProps?.attributes === "function"
          ? editorProps?.attributes(state)
          : editorProps?.attributes),
      }),
    },
  });

  // useEditor reads editable only at creation; push later changes (e.g. live lock toggle) in.
  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  return editor;
}
